import { escapeHtml as esc } from '../../../core/ui/escapeHtml.js';
import { openMiniModalConfirm, openMiniModalOk } from '../../../core/ui/miniModal.js';
import { firebaseErrorHintEs } from '../../../core/errors/firebaseErrorHintEs.js';

function iconBack() {
  return `<svg class="h-6 w-6 text-brand-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>`;
}

function formatPrecioAr(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return x.toLocaleString('es-AR', { maximumFractionDigits: 0 });
}

function shellLoading() {
  return `
      <div class="min-h-screen bg-brand-surface mlv-bg-surface pb-10">
        <header class="sticky top-0 z-10 flex items-center gap-2 border-b border-slate-200/80 bg-brand-surface/95 px-3 py-3 backdrop-blur-sm">
          <button type="button" id="pv-back" class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-slate-200/60" aria-label="Volver">
            ${iconBack()}
          </button>
          <h1 class="min-w-0 flex-1 font-heading text-base font-bold tracking-tight text-brand-primary">Vecino en el barrio</h1>
        </header>
        <main class="mx-auto max-w-lg px-4 pt-4">
          <p class="text-center text-sm text-slate-600">Cargando…</p>
        </main>
      </div>
    `;
}

/** Perfil público por UID (solo productos publicados; sin DNI ni vitrina). */
function htmlDenunciaUbicacionBarrio() {
  return `<div class="relative z-10 mt-4 border-t border-slate-200/80 pt-4">
    <button type="button" id="pv-denuncia-ubicacion" class="w-full cursor-pointer touch-manipulation text-left text-[11px] font-medium text-slate-500 underline decoration-slate-400 underline-offset-2 hover:text-amber-900">
      Perfil ajeno al barrio — denunciar
    </button>
  </div>`;
}

export function createPerfilVentasPresenter({
  render,
  perfilVentasRepository,
  catalogoRepository,
  getFirebase,
  denunciasBarrioRepository = null,
}) {
  function esUidReportable(raw) {
    const s = String(raw || '').trim();
    return /^[A-Za-z0-9_-]{10,128}$/.test(s);
  }

  function wireDenunciarUbicacion(reportadoUid) {
    const uidRep = String(reportadoUid || '').trim();
    const fb = typeof getFirebase === 'function' ? getFirebase() : null;
    const viewer = String(fb?.auth?.currentUser?.uid || '').trim();
    const btn = document.getElementById('pv-denuncia-ubicacion');
    if (!btn || !denunciasBarrioRepository) return;
    if (!viewer || viewer === uidRep) return;
    if (!esUidReportable(uidRep)) return;
    btn.addEventListener('click', async (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      try {
        const confirma = await openMiniModalConfirm({
          title: '¿Confirmás que este vendedor no pertenece al barrio?',
          paragraphs: [
            'Al reportar a un usuario como "ajeno a la zona", nuestro equipo revisará su perfil.',
            'Tené en cuenta que las denuncias falsas afectan la convivencia y pueden resultar en la suspensión de tu cuenta.',
          ],
          confirmLabel: 'Denunciar',
          cancelLabel: 'Cancelar',
        });
        if (!confirma) return;
        const r = await denunciasBarrioRepository.registrarDenunciaUbicacion(uidRep);
        if (r.yaDenunciado) {
          await openMiniModalOk({
            title: 'Listo',
            message: 'Ya habíamos registrado tu reporte sobre este perfil.',
            okLabel: 'Cerrar',
          });
          return;
        }
        await openMiniModalOk({
          title: 'Gracias',
          message: 'Recibimos tu reporte.',
          okLabel: 'Cerrar',
        });
      } catch (e) {
        console.error(e);
        const hint = firebaseErrorHintEs(e);
        await openMiniModalOk({
          title: 'No se pudo enviar',
          message: `${e?.message || 'Probá de nuevo.'}${hint ? ` ${hint}` : ''}`,
          okLabel: 'Cerrar',
        });
      }
    });
  }

  async function show({ vendedorUid, onBack, onOpenProducto } = {}) {
    const uidRaw = String(vendedorUid || '').trim();
    const uidOk = /^[A-Za-z0-9_-]{10,128}$/.test(uidRaw);

    render(shellLoading());
    document.getElementById('pv-back')?.addEventListener('click', () => onBack?.());

    if (!uidOk) {
      render(`
        <div class="min-h-screen bg-brand-surface mlv-bg-surface px-4 pb-10 pt-4">
          <header class="mb-4 flex items-center gap-2">
            <button type="button" id="pv-back-err" class="flex h-10 w-10 items-center justify-center rounded-full hover:bg-slate-200/60">${iconBack()}</button>
            <h1 class="font-heading text-base font-bold text-brand-primary">Vecino en el barrio</h1>
          </header>
          <p class="text-sm text-red-700">Enlace inválido. Usá <span class="font-mono text-xs">#/perfil-vendedor/TU_UID</span>.</p>
        </div>
      `);
      document.getElementById('pv-back-err')?.addEventListener('click', () => onBack?.());
      return;
    }

    let items = [];
    try {
      items = await catalogoRepository.listarProductosPublicadosPorVendedorUid(uidRaw, 48);
    } catch (e) {
      console.error(e);
    }
    const haySesion = Boolean(getFirebase?.().auth?.currentUser);
    let nombreTitulo = '';
    try {
      nombreTitulo = await perfilVentasRepository.obtenerNombrePublicoPorUid(uidRaw);
    } catch (_e) {
      nombreTitulo = '';
    }
    const nombreParaPerfil = String(nombreTitulo || '').trim() || 'Vecino';
    const listHtml =
      items.length === 0
        ? `<p class="rounded-xl border border-slate-200 bg-white p-4 text-center text-sm text-slate-600">No hay productos publicados en el catálogo con esta cuenta.</p>`
        : `<ul class="grid grid-cols-2 gap-3">
          ${items
            .map((it) => {
              const fotos = Array.isArray(it.fotos) ? it.fotos : [];
              const img = fotos[0]
                ? `<img src="${esc(fotos[0])}" alt="" class="h-full w-full object-cover" loading="lazy" />`
                : `<div class="flex h-full w-full items-center justify-center bg-slate-200 text-xs text-slate-500">Sin foto</div>`;
              return `<li>
                <button type="button" class="block w-full overflow-hidden rounded-2xl bg-white text-left shadow-[0_2px_10px_rgba(0,0,0,0.06)] ring-1 ring-slate-200/70 transition hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)]" data-pv-prod="${esc(it.id)}">
                  <div class="relative aspect-square w-full overflow-hidden bg-slate-200">${img}</div>
                  <div class="px-3 pb-3 pt-2.5">
                    <span class="line-clamp-2 text-xs font-bold text-slate-900">${esc(it.titulo || 'Sin título')}</span>
                    <span class="mt-0.5 block text-sm font-bold text-brand-tertiary">$ ${esc(formatPrecioAr(it.precio))}</span>
                  </div>
                </button>
              </li>`;
            })
            .join('')}
        </ul>`;
    render(`
    <div class="min-h-screen bg-brand-surface mlv-bg-surface pb-10">
      <header class="sticky top-0 z-10 flex items-center gap-2 border-b border-slate-200/80 bg-brand-surface/95 px-3 py-3 backdrop-blur-sm">
        <button type="button" id="pv-back" class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-slate-200/60" aria-label="Volver">
          ${iconBack()}
        </button>
        <h1 class="min-w-0 flex-1 font-heading text-base font-bold tracking-tight text-brand-primary">Vecino en el barrio</h1>
      </header>
      <main class="mx-auto max-w-lg px-4 pt-4 space-y-4">
        <div class="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm">
          <p class="font-heading text-lg font-bold leading-snug text-slate-900">
            Perfil de venta de <span class="text-brand-primary">"${esc(nombreParaPerfil)}"</span>
          </p>
          <p class="mt-2 text-xs text-slate-500">${esc(String(items.length))} publicación(es) en el catálogo</p>
        </div>
        <div>
          <h2 class="mb-2 font-heading text-sm font-bold text-slate-800">Productos publicados</h2>
          ${listHtml}
        </div>
        ${haySesion ? htmlDenunciaUbicacionBarrio() : ''}
      </main>
    </div>
  `);
    document.getElementById('pv-back')?.addEventListener('click', () => onBack?.());
    document.querySelectorAll('[data-pv-prod]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-pv-prod');
        if (id) onOpenProducto?.(id);
      });
    });
    if (haySesion) wireDenunciarUbicacion(uidRaw);
  }

  return { show };
}
